import React, { useState, useMemo } from "react";
import {
  useDeleteUserMutation,
  useFetchPersonelsQuery,
  useFetchTeamsQuery,
} from "../../store";
import Loading from "../../components/shared/Loading";
import PersonelCard from "./components/PersonelCard";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import SearchBox from "./components/SearchBox";
import { useDispatch, useSelector } from "react-redux";
import StartLeaveForm from "./components/StartLeaveForm";
import {
  openErrorToast,
  openSuccessToast,
} from "../../store/slices/toast/toastSlice";
import { setSelectedId } from "../../store/slices/user/userSlice";
import { closeDialog } from "../../store/slices/dialog/dialogSlice";
import { CONFIRM_DIALOG } from "../../store/slices/utils/dialogEnum";
import CustomConfirmDialog from "../../components/shared/CustomConfirmDialog";
import EditDayoffs from "./components/EditDayoffs";
import { LEAVED, WORKING, SAVED } from "../../workingStatus";

const PersonelList = () => {
  const dispatch = useDispatch();
  const [searchText, setSearchText] = React.useState("");
  const [teamFilter, setTeamFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);

  const { startLeaveDialogOpen, confirmDialogOpen, editDayoffDialogOpen } =
    useSelector((state) => state.dialogSlice);
  const { selectedUserId } = useSelector((state) => state.userSlice);

  const { data: personels, isLoading } = useFetchPersonelsQuery(
    { page: 1, limit: 9999, searchText: "", date: "" },
    { refetchOnMountOrArgChange: true }
  );

  const { data: teams } = useFetchTeamsQuery();

  const [deletePersonel, { isLoading: isDeleting }] = useDeleteUserMutation();

  const handleDelete = () => {
    deletePersonel(selectedUserId).then((res) => {
      if (!res.data.isSuccess) {
        dispatch(openErrorToast(res.data.message));
      } else {
        dispatch(openSuccessToast("Kullanıcı Silindi !"));
        handleDeleteClose();
      }
    });
  };

  const handleDeleteClose = () => {
    dispatch(setSelectedId(null));
    dispatch(closeDialog(CONFIRM_DIALOG));
  };

  // Sıralama + Filtreleme (frontend)
  const processedPersonels = useMemo(() => {
    if (!personels?.data) return [];

    const search = searchText.trim().toLocaleLowerCase("tr");

    return [...personels.data]
      // İsim araması (frontend)
      .filter((p) => {
        if (!search) return true;
        const fullName = `${p.firstName} ${p.lastName}`.toLocaleLowerCase("tr");
        return fullName.includes(search);
      })
      // Durum filtresi
      .filter((p) => {
        if (statusFilter === null) return true;
        if (statusFilter === LEAVED) {
          // Gerçek ayrılanlar: hem LEAVED statüsü hem de ayrılış tarihi olmalı
          return p.workingStatusId === LEAVED && p.workingEndDate;
        }
        return p.workingStatusId === statusFilter;
      })
      // Ekip filtresi (teamNames string içinde arar)
      .filter((p) =>
        teamFilter === null
          ? true
          : p.teamNames && p.teamNames.includes(teamFilter)
      )
      // Sıralama: önce aktifler alfabetik, ayrılanlar en sona
      .sort((a, b) => {
        const aLeaved = a.workingStatusId === LEAVED;
        const bLeaved = b.workingStatusId === LEAVED;
        if (aLeaved && !bLeaved) return 1;
        if (!aLeaved && bLeaved) return -1;
        const aName = `${a.firstName} ${a.lastName}`.toLocaleLowerCase("tr");
        const bName = `${b.firstName} ${b.lastName}`.toLocaleLowerCase("tr");
        return aName.localeCompare(bName, "tr");
      });
  }, [personels?.data, searchText, statusFilter, teamFilter]);

  // İstatistikler (tüm veriden — filtre uygulanmadan)
  const stats = useMemo(() => {
    const all = personels?.data || [];
    return {
      total: all.length,
      active: all.filter((p) => p.workingStatusId === WORKING).length,
      saved: all.filter((p) => p.workingStatusId === SAVED).length,
      leaved: all.filter((p) => p.workingStatusId === LEAVED).length,
    };
  }, [personels?.data]);

  if (isLoading) return <Loading isLoading={isLoading} />;

  return (
    <>
      {startLeaveDialogOpen && <StartLeaveForm />}
      {editDayoffDialogOpen && <EditDayoffs />}
      {confirmDialogOpen && (
        <CustomConfirmDialog
          title="Personel Sil"
          text="Personeli Silmek İstediğinize Emin Misiniz ?"
          handleConfirm={handleDelete}
          isLoading={isDeleting}
          handleClose={handleDeleteClose}
        />
      )}
      <SearchBox
        searchText={searchText}
        setSearchText={setSearchText}
        teams={teams?.data || []}
        teamFilter={teamFilter}
        setTeamFilter={setTeamFilter}
      />
      {/* İstatistik Kartları */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
          gap: { xs: 1.5, sm: 2 },
          px: { xs: 1, sm: 2, md: 4 },
          pt: 1,
          pb: 1,
        }}
      >
        {[
          {
            label: "Toplam Personel",
            value: stats.total,
            color: "#8B1A1A",
            bg: "#fef2f2",
            border: "#fca5a5",
            filterVal: null,
          },
          {
            label: "Aktif",
            value: stats.active,
            color: "#16a34a",
            bg: "#f0fdf4",
            border: "#86efac",
            filterVal: WORKING,
          },
          {
            label: "İzinli",
            value: stats.saved,
            color: "#d97706",
            bg: "#fffbeb",
            border: "#fcd34d",
            filterVal: SAVED,
          },
          {
            label: "Ayrılan",
            value: stats.leaved,
            color: "#64748b",
            bg: "#f8fafc",
            border: "#cbd5e1",
            filterVal: LEAVED,
          },
        ].map((stat) => (
          <Box
            key={stat.label}
            onClick={() =>
              setStatusFilter(
                statusFilter === stat.filterVal ? null : stat.filterVal
              )
            }
            sx={{
              bgcolor: stat.bg,
              border: `1.5px solid ${
                statusFilter === stat.filterVal ? stat.color : stat.border
              }`,
              borderRadius: 3,
              p: { xs: 1.5, sm: 2 },
              cursor: "pointer",
              transition: "all 0.18s",
              boxShadow:
                statusFilter === stat.filterVal
                  ? `0 4px 16px ${stat.color}33`
                  : "none",
              transform:
                statusFilter === stat.filterVal ? "scale(1.03)" : "scale(1)",
              "&:hover": {
                boxShadow: `0 4px 16px ${stat.color}22`,
                transform: "scale(1.02)",
              },
            }}
          >
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{ color: stat.color, lineHeight: 1 }}
            >
              {stat.value}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: stat.color, mt: 0.5, opacity: 0.8 }}
            >
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Box>
      <Divider sx={{ mb: 2, mt: 1 }} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr 1fr",
          },
          gap: { xs: 1.5, sm: 2, md: 3 },
          width: "100%",
          px: { xs: 1, sm: 2, md: 4 },
          py: 2,
          minHeight: "40vh",
        }}
      >
        {processedPersonels.length === 0 ? (
          <Box sx={{ gridColumn: "1/-1", textAlign: "center", py: 6 }}>
            <Typography variant="h6" color="text.secondary">
              Kayıt Bulunamadı
            </Typography>
          </Box>
        ) : (
          processedPersonels.map((personel) => (
            <PersonelCard key={personel.id} personel={personel} />
          ))
        )}
      </Box>
    </>
  );
};

export default PersonelList;
